import { isAxiosError } from "axios";

import { faro, LogLevel } from "@grafana/faro-web-sdk";
import { streamNotifications } from "@vega-api/generated/notifications-v1/notifications-v1";

const RECONNECT_BASE_DELAY_MS = 1_000;
const RECONNECT_MAX_DELAY_MS = 30_000;

type StreamCallbacks = {
  onOpen: () => void;
  onEvent: (eventName: string, data: unknown) => void;
};

type ParsedFrame = {
  eventName: string;
  data: string;
  id?: string;
};

const isAbortError = (error: unknown): boolean =>
  error instanceof Error &&
  (error.name === "AbortError" || error.name === "CanceledError");

const isFatalStatus = (status: number | undefined): boolean =>
  status === 401 || status === 403;

const reconnectDelay = (attempt: number): number =>
  Math.min(RECONNECT_BASE_DELAY_MS * 2 ** attempt, RECONNECT_MAX_DELAY_MS);

const wait = (ms: number, signal: AbortSignal): Promise<void> =>
  new Promise((resolve) => {
    if (signal.aborted) {
      resolve();

      return;
    }

    const timer = setTimeout(resolve, ms);

    signal.addEventListener(
      "abort",
      () => {
        clearTimeout(timer);
        resolve();
      },
      { once: true },
    );
  });

const parseFrame = (frame: string): ParsedFrame | null => {
  let eventName = "message";
  let id: string | undefined;
  const dataLines: string[] = [];

  for (const line of frame.split("\n")) {
    if (line === "" || line.startsWith(":")) continue;

    const separator = line.indexOf(":");
    const field = separator === -1 ? line : line.slice(0, separator);
    const rawValue = separator === -1 ? "" : line.slice(separator + 1);
    const value = rawValue.startsWith(" ") ? rawValue.slice(1) : rawValue;

    if (field === "event") eventName = value;
    else if (field === "data") dataLines.push(value);
    else if (field === "id") id = value;
  }

  if (dataLines.length === 0) return null;

  return { eventName, data: dataLines.join("\n"), id };
};

const dispatchFrame = (
  frame: ParsedFrame,
  onEvent: StreamCallbacks["onEvent"],
) => {
  try {
    onEvent(frame.eventName, JSON.parse(frame.data));
  } catch (error) {
    faro.api.pushError(error as Error, {
      type: "notifications-sse",
      context: { source: "notifications-sse", event: frame.eventName },
    });
  }
};

const flushSseBuffer = (
  buffer: string,
  onFrame: (frame: ParsedFrame) => void,
): string => {
  let boundary = buffer.indexOf("\n\n");

  while (boundary !== -1) {
    const parsed = parseFrame(buffer.slice(0, boundary));

    if (parsed) onFrame(parsed);

    buffer = buffer.slice(boundary + 2);
    boundary = buffer.indexOf("\n\n");
  }

  return buffer;
};

const readEventStream = async (
  stream: ReadableStream<Uint8Array>,
  signal: AbortSignal,
  onEvent: StreamCallbacks["onEvent"],
  initialEventId: string | undefined,
  onActive: () => void,
): Promise<string | undefined> => {
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  const cancelRead = () => void reader.cancel();

  signal.addEventListener("abort", cancelRead, { once: true });

  let buffer = "";
  let lastEventId = initialEventId;
  let activated = false;

  if (signal.aborted) {
    await reader.cancel();

    return lastEventId;
  }

  try {
    let result = await reader.read();

    while (!result.done) {
      const chunk = decoder
        .decode(result.value, { stream: true })
        .replace(/\r\n/g, "\n");

      buffer = flushSseBuffer(buffer + chunk, (frame) => {
        if (!activated) {
          activated = true;
          onActive();
        }

        if (frame.id !== undefined) lastEventId = frame.id;
        dispatchFrame(frame, onEvent);
      });

      result = await reader.read();
    }
  } finally {
    signal.removeEventListener("abort", cancelRead);
  }

  return lastEventId;
};

const runStream = async (
  { onOpen, onEvent }: StreamCallbacks,
  signal: AbortSignal,
): Promise<void> => {
  const aborted = () => signal.aborted;
  let attempt = 0;
  let hasReportedError = false;
  let lastEventId: string | undefined;

  const markHealthy = () => {
    attempt = 0;
    hasReportedError = false;
  };

  while (!aborted()) {
    try {
      const stream = (await streamNotifications(
        {
          adapter: "fetch",
          responseType: "stream",
          timeout: 0,
          headers: {
            Accept: "text/event-stream",
            ...(lastEventId != null ? { "Last-Event-ID": lastEventId } : {}),
          },
        },
        signal,
      )) as unknown as ReadableStream<Uint8Array>;

      if (aborted()) {
        void stream.cancel();

        return;
      }

      onOpen();

      lastEventId = await readEventStream(
        stream,
        signal,
        onEvent,
        lastEventId,
        markHealthy,
      );
    } catch (error) {
      if (aborted() || isAbortError(error)) return;

      const status = isAxiosError(error) ? error.response?.status : undefined;

      if (isFatalStatus(status)) {
        faro.api.pushError(error as Error, {
          type: "notifications-sse",
          context: { source: "notifications-sse", fatal: "true" },
        });

        return;
      }

      if (!hasReportedError) {
        hasReportedError = true;
        faro.api.pushLog(["Notifications SSE stream errored"], {
          level: LogLevel.WARN,
          context: { source: "notifications-sse" },
        });
      }
    }

    if (aborted()) return;

    await wait(reconnectDelay(attempt), signal);
    attempt += 1;
  }
};

export const connectNotificationsStream = (
  callbacks: StreamCallbacks,
): (() => void) => {
  const controller = new AbortController();

  void runStream(callbacks, controller.signal);

  return () => controller.abort();
};
